import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { socket, SocketEvents } from 'socket'
import { useAppDispatch, useAppSelector } from 'store'
import useLiveCall from 'hooks/use-live-call'
import { useAppContext } from 'modules/App/AppContext'
import { useLoginMaster } from 'queries/master'
import { useGetNotificationsCount } from 'queries/notifications-count'
import { url as pendingApprovalQuotationUrl } from 'modules/Opportunities/PendingApprovalQuotation'

import { Flex, Icon, Modal, Text } from 'components'

import useModal from './use-modal'

const useAppSetup = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { getStatus: getLiveCallStatus } = useLiveCall()
  const { setSocketConnected } = useAppContext()
  const { setModal } = useModal()
  const [master] = useLoginMaster()
  const masterId = master?.id
  const user = useAppSelector((state) => state.user.user)
  const selectedBranchId = useAppSelector((state) => state.user.selectedBranch?.branchId)
  const isLoginMessageCleared = localStorage.getItem('isLoginMessageCleared') === 'true'
  const { data: pendingApprovalQuotation } = useGetNotificationsCount({ enabled: !!selectedBranchId })
  const pendingQuotationTotalCount = pendingApprovalQuotation?.total_pending_approval_quotation_count || 0

  useEffect(() => {
    const onConnectSocket = () => setSocketConnected(true)
    const onDisconnectSocket = () => setSocketConnected(false)

    if (masterId && user?.id) {
      socket.init(masterId, user?.id, dispatch, queryClient)
      socket.addListener(SocketEvents.CONNECT, onConnectSocket)
      socket.addListener(SocketEvents.DISCONNECT, onDisconnectSocket)
    }

    return () => {
      socket.removeListener(SocketEvents.CONNECT, onConnectSocket)
      socket.removeListener(SocketEvents.DISCONNECT, onDisconnectSocket)
      socket.disconnect()
    }
  }, [dispatch, user, masterId, queryClient, setSocketConnected])

  useEffect(() => {
    if (selectedBranchId) {
      getLiveCallStatus()
    }
  }, [dispatch, getLiveCallStatus, selectedBranchId])

  useEffect(() => {
    if (!isLoginMessageCleared && pendingQuotationTotalCount > 0) {
      setModal(
        <Modal
          visible
          cancelText="dismiss_lbl"
          okText="view_quotation_lbl"
          onClose={() => {
            localStorage.setItem('isLoginMessageCleared', 'true')
            setModal(null)
          }}
          onOk={() => {
            localStorage.setItem('isLoginMessageCleared', 'true')
            navigate(pendingApprovalQuotationUrl)
            setModal(null)
          }}
        >
          <Flex isColumn alignItems="center">
            <Icon icon={['far', 'clock']} fontSize={30} color="orange1" />
            <Text variant="h5">{t('pending_quotation_approval_count_msg', { count: pendingQuotationTotalCount })}</Text>
            <Text color="gray5" variant="h6">
              click_to_view_pending_quotation_msg
            </Text>
          </Flex>
        </Modal>,
      )
    }
  }, [setModal, pendingQuotationTotalCount, isLoginMessageCleared, t, navigate])
}

export default useAppSetup
